import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import ProtectedRoute from "./components/ProtectedRoute";
import MainLayout from "./layouts/MainLayout";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Profile from "./pages/Profile";
import Orders from "./pages/Orders";
import Logistics from "./pages/Logistics";
import Maintenance from "./pages/Maintenance";
import Checkout from "./pages/Checkout";
import AdminDashboard from "./pages/admin/Dashboard";
import ProductManagement from "./pages/admin/ProductManagement";
import OrderManagement from "./pages/admin/OrderManagement";
import ProductForm from "./pages/admin/ProductForm";
import ServiceRequests from "./pages/admin/ServiceRequests";
import Reports from "./pages/admin/Reports";
import CRM from "./pages/admin/CRM";
import CatalogSync from "./pages/admin/CatalogSync";
import SupplierPayments from "./pages/admin/SupplierPayments";
import WarrantyManagement from "./pages/WarrantyManagement";
import EmailMarketing from "./pages/admin/EmailMarketing";
import UserManagement from "./pages/admin/UserManagement";
import ContentManagement from "./pages/admin/ContentManagement";
import SellerReviews from "./pages/admin/SellerReviews";
import CategoryDetails from "./pages/CategoryDetails";
import Reviews from "./pages/Reviews";
import MediBlog from "./pages/MediBlog";
import MediBlogArticlePage from "./pages/MediBlogArticle";

function App() {
  return (
    <ThemeProvider>
      <CssBaseline />
      <AuthProvider>
        <CartProvider>
          <Router>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* Main layout routes */}
              <Route path="/" element={<MainLayout />}>
                <Route index element={<Navigate to="/products" replace />} />
                <Route path="products" element={<Products />} />
                <Route path="category/:categoryId/*" element={<CategoryDetails />} />
                <Route path="products/:id" element={<ProductDetail />} />
                <Route path="cart" element={<Cart />} />
                <Route path="reviews" element={<Reviews />} />
                <Route path="mediblog" element={<MediBlog />} />
                <Route path="mediblog/:slug" element={<MediBlogArticlePage />} />

                {/* Protected routes */}
                <Route
                  path="profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="orders"
                  element={
                    <ProtectedRoute>
                      <Orders />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="checkout"
                  element={
                    <ProtectedRoute>
                      <Checkout />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="logistics"
                  element={
                    <ProtectedRoute>
                      <Logistics />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="maintenance"
                  element={
                    <ProtectedRoute>
                      <Maintenance />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="warranties"
                  element={
                    <ProtectedRoute>
                      <WarrantyManagement />
                    </ProtectedRoute>
                  }
                />

                {/* Admin routes */}
                <Route
                  path="admin/dashboard"
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/products"
                  element={
                    <ProtectedRoute adminOnly>
                      <ProductManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/orders"
                  element={
                    <ProtectedRoute adminOnly>
                      <OrderManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/products/new"
                  element={
                    <ProtectedRoute adminOnly>
                      <ProductForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/products/:id/edit"
                  element={
                    <ProtectedRoute adminOnly>
                      <ProductForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/service-requests"
                  element={
                    <ProtectedRoute adminOnly>
                      <ServiceRequests />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/reports"
                  element={
                    <ProtectedRoute adminOnly>
                      <Reports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/crm"
                  element={
                    <ProtectedRoute adminOnly>
                      <CRM />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/catalog-sync"
                  element={
                    <ProtectedRoute adminOnly>
                      <CatalogSync />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/payments"
                  element={
                    <ProtectedRoute adminOnly>
                      <SupplierPayments />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/email-marketing"
                  element={
                    <ProtectedRoute adminOnly>
                      <EmailMarketing />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/users"
                  element={
                    <ProtectedRoute adminOnly>
                      <UserManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/cms"
                  element={
                    <ProtectedRoute adminOnly>
                      <ContentManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/reviews"
                  element={
                    <ProtectedRoute adminOnly>
                      <SellerReviews />
                    </ProtectedRoute>
                  }
                />

                {/* Seller routes */}
                <Route
                  path="seller/products"
                  element={
                    <ProtectedRoute sellerOnly>
                      <ProductManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="seller/products/new"
                  element={
                    <ProtectedRoute sellerOnly>
                      <ProductForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="seller/products/:id/edit"
                  element={
                    <ProtectedRoute sellerOnly>
                      <ProductForm />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/products" replace />} />
            </Routes>
          </Router>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
